import { skipToken } from '@reduxjs/toolkit/query'
import {
  selectFinancialYear,
  selectProgramType,
} from 'legacy/lut/features/partner360/configuration'
import { selectJBPSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { TKeyInitiative, useGetKeyInitiativesQuery } from 'partner360/entities/keyInitiatives'

import { mapActivitiesWithBusinessPriorities } from '../helpers/activityBusinessPriorities'
import {
  requestMdfActivitiesData,
  selectIsMdfActivitiesFetching,
  selectMdfActivities,
} from '../model'

import { useMdfDashboardFiltersForm } from './useMdfDashboardFiltersForm'

const EMPTY_INITIATIVES: TKeyInitiative[] = []

export const useMdfAnalysis = (partnerId: string) => {
  const dispatch = useAppDispatch()
  const fYear = useSelector(selectFinancialYear)
  const programType = useSelector(selectProgramType)

  const mdfActivities = useSelector(selectMdfActivities)
  const isFetchingMdfActivities = useSelector(selectIsMdfActivitiesFetching)
  const { kiBusinessPriorities } = useSelector(selectJBPSettings)

  const {
    data: initiatives = EMPTY_INITIATIVES,
    refetch: refetchInitiatives,
    isFetching: isFetchingInitiatives,
  } = useGetKeyInitiativesQuery(partnerId && fYear ? { partnerId, fYear, programType } : skipToken)

  const refetchMdfActivities = useCallback(() => {
    dispatch(requestMdfActivitiesData({ partnerId, fYear, programType }))
  }, [dispatch, partnerId, fYear, programType])

  const { strategicInitiativesFilterValue, businessPrioritiesFilterValue, timeframesFilterValue } =
    useMdfDashboardFiltersForm()

  const activitiesWithBusinessPriorities = useMemo(
    () => mapActivitiesWithBusinessPriorities(mdfActivities, initiatives, kiBusinessPriorities),
    [mdfActivities, initiatives, kiBusinessPriorities]
  )

  const filteredActivities = useMemo(
    () =>
      activitiesWithBusinessPriorities.filter((activity) => {
        const matchesStrategicInitiative =
          !strategicInitiativesFilterValue?.length ||
          strategicInitiativesFilterValue.some((value) =>
            activity.strategicInitiatives.includes(value)
          )

        const matchesBusinessPriority =
          !businessPrioritiesFilterValue?.length ||
          businessPrioritiesFilterValue.some((value) => activity.businessPriorities.includes(value))

        const matchesTimeframe =
          !timeframesFilterValue?.length || timeframesFilterValue.includes(activity.timeframe)

        return matchesStrategicInitiative && matchesBusinessPriority && matchesTimeframe
      }),
    [
      activitiesWithBusinessPriorities,
      strategicInitiativesFilterValue,
      businessPrioritiesFilterValue,
      timeframesFilterValue,
    ]
  )

  return {
    initiatives,
    refetchInitiatives,
    isFetchingInitiatives,
    mdfActivities,
    filteredActivities,
    isFetchingMdfActivities,
    refetchMdfActivities,
  }
}
